"use client";

import React, { useState, useEffect, useMemo } from "react";
import { motion } from "motion/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import api from "../lib/api";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, Shield } from "lucide-react";

const INSURANCE_PROVIDERS = [
  "Allianz Care Canada",
  "Alberta Motor Association Insurance Company",
  "Arch Insurance Canada Ltd.",
  "Aviva Canada",
  "Belair Insurance Company Inc.",
  "Beneva",
  "Blue Cross (provincial)",
  "BMO Insurance",
  "Canada Life Assurance Company",
  "Canadian Farm Insurance Corporation",
  "Canadian Premier Life Insurance Company",
  "Canassurance Hospital Service Association",
  "Chubb Life Insurance Company of Canada",
  "CIGNA Life Insurance Company of Canada",
  "Co-operators Life Insurance Company",
  "Desjardins Insurance",
  "Empire Life Insurance",
  "Fairfax Financial Holdings",
  "Foresters Financial",
  "Great-West Lifeco",
  "Green Shield Canada",
  "Humania Assurance Inc.",
  "iA Financial Corporation (Industrial Alliance)",
  "Intact Financial Corporation",
  "JEVCO Insurance Company",
  "Johnson Insurance",
  "La Capitale Financial Security",
  "Manulife",
  "MAX Insurance",
  "Medavie Blue Cross",
  "Northbridge Financial Corporation",
  "Optimum Insurance Company Inc.",
  "Peace Hills General Insurance Company",
  "Promutuel Assurance",
  "RBC Insurance",
  "SSQ Insurance",
  "Sun Life Financial",
  "The Mutual Fire Insurance Company of British Columbia",
  "Zurich Insurance Company Ltd. (Canada)",
  "Other",
].sort();

const formSchema = z.object({
  isInsuredMember: z.string(),
  insuredMemberName: z.string().min(2, "Policy holder name is required"),
  insuredMemberDob: z.string().min(1, "Policy holder date of birth is required"),
  relationshipWithInsuredMember: z.string().optional(),
  dependentName: z.string().optional(),
  dependentDob: z.string().optional(),
  insuranceCompany: z.string().min(1, "Insurance provider is required"),
  groupNumber: z.string().optional(),
  certificateNumber: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.isInsuredMember === "no") {
    if (!data.relationshipWithInsuredMember) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Relationship is required",
        path: ["relationshipWithInsuredMember"],
      });
    }
    if (!data.dependentName || data.dependentName.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Dependent name is required",
        path: ["dependentName"],
      });
    }
    if (!data.dependentDob) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Dependent date of birth is required",
        path: ["dependentDob"],
      });
    }
  }
});

const normalizeDateForInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().split("T")[0];
};

export default function InsuranceForm({
  clientEmail,
  clientName,
  clientDob,
  bookingId,
  onComplete,
}) {
  const [initialLoading, setInitialLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [insuranceCardFront, setInsuranceCardFront] = useState(null);
  const [insuranceCardBack, setInsuranceCardBack] = useState(null);
  const [existingFrontUrl, setExistingFrontUrl] = useState("");
  const [existingBackUrl, setExistingBackUrl] = useState("");

  const getClientEmailFromToken = () => {
    try {
      const token = localStorage.getItem("bookatherapy_token");
      if (!token) return "";
      const base64Url = token.split(".")[1];
      if (!base64Url) return "";
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      const payload = JSON.parse(jsonPayload);
      return (payload.email || payload.userEmail || "").trim();
    } catch (err) {
      console.warn("Unable to derive client email from token:", err);
      return "";
    }
  };

  const resolvedClientEmail = useMemo(() => {
    const fromProps = (clientEmail || "").trim();
    if (fromProps) return fromProps;
    return getClientEmailFromToken();
  }, [clientEmail]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isInsuredMember: "yes",
      insuredMemberName: clientName || "",
      insuredMemberDob: normalizeDateForInput(clientDob) || "",
      relationshipWithInsuredMember: "",
      dependentName: clientName || "",
      dependentDob: normalizeDateForInput(clientDob) || "",
      insuranceCompany: "",
      groupNumber: "",
      certificateNumber: "",
    },
  });

  const isInsuredMember = form.watch("isInsuredMember");

  useEffect(() => {
    const fetchExistingDetails = async () => {
      if (!resolvedClientEmail) return;

      try {
        setInitialLoading(true);
        // Use optional chaining just in case api.insurance isn't fully ready
        const data = await api.insurance?.get({ email: resolvedClientEmail }).catch(() => null);
        
        if (data?.insuranceDetails) {
          const details = data.insuranceDetails;
          form.reset({
            isInsuredMember: details.isInsuredMember ? "yes" : "no",
            insuredMemberName: details.insuredMemberName || "",
            insuredMemberDob: normalizeDateForInput(details.insuredMemberDob) || "",
            relationshipWithInsuredMember: details.relationshipWithInsuredMember || "",
            dependentName: details.dependentName || clientName || "",
            dependentDob: normalizeDateForInput(details.dependentDob || clientDob) || "",
            insuranceCompany: details.insuranceCompany || "",
            groupNumber: details.groupNumber || "",
            certificateNumber: details.certificateNumber || "",
          });
          setExistingFrontUrl(details.insuranceCardFrontUrl || "");
          setExistingBackUrl(details.insuranceCardBackUrl || "");
        }
      } catch (err) {
        console.warn("Error fetching existing insurance details:", err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchExistingDetails();
  }, [resolvedClientEmail, form, clientName, clientDob]);

  const validateFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) return "Only JPEG, PNG, or PDF files are allowed.";
    if (file.size > 5 * 1024 * 1024) return "File size must be less than 5MB.";
    return null;
  };

  const uploadFiles = async () => {
    const urls = { front: null, back: null };
    if (insuranceCardFront) {
      const err = validateFile(insuranceCardFront);
      if (err) throw new Error(`Front card: ${err}`);
      const res = await api.insurance.uploadCard(insuranceCardFront);
      if (res.url) urls.front = res.url;
    }
    if (insuranceCardBack) {
      const err = validateFile(insuranceCardBack);
      if (err) throw new Error(`Back card: ${err}`);
      const res = await api.insurance.uploadCard(insuranceCardBack);
      if (res.url) urls.back = res.url;
    }
    return urls;
  };

  const onSubmit = async (values) => {
    if (!resolvedClientEmail) {
      toast.error("Sign in again to save insurance details.");
      return;
    }

    try {
      setSubmitting(true);
      const urls = await uploadFiles();
      
      const frontUrl = urls.front || existingFrontUrl;
      const backUrl = urls.back || existingBackUrl;

      const payload = {
        clientEmail: resolvedClientEmail,
        clientName,
        bookingId,
        isInsuredMember: values.isInsuredMember === "yes",
        insuredMemberName: values.insuredMemberName,
        insuredMemberDob: values.insuredMemberDob,
        relationshipWithInsuredMember: values.isInsuredMember === "yes" ? undefined : values.relationshipWithInsuredMember,
        dependentName: values.isInsuredMember === "yes" ? undefined : values.dependentName,
        dependentDob: values.isInsuredMember === "yes" ? undefined : values.dependentDob,
        insuranceCompany: values.insuranceCompany,
        groupNumber: values.groupNumber || undefined,
        certificateNumber: values.certificateNumber || undefined,
        insuranceCardFrontUrl: frontUrl,
        insuranceCardBackUrl: backUrl,
      };

      const result = await api.insurance.create(payload);
      toast.success("Insurance details saved successfully.");
      
      if (onComplete) {
        onComplete(result?.insuranceDetails || {
          insuranceCardFrontUrl: frontUrl,
          insuranceCardBackUrl: backUrl,
        });
      }
    } catch (err) {
      toast.error(err.message || "Failed to save insurance details.");
    } finally {
      setSubmitting(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
        <p className="mt-4 text-sm text-slate-500 font-medium tracking-tight">Syncing coverage details...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm">
          <Shield className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-xl font-bold tracking-tight text-slate-900 leading-tight">
            Insurance Coverage
          </h3>
          <p className="text-sm text-slate-500 font-medium">
            Enter policy details for direct billing processing.
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="isInsuredMember"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel className="text-sm font-bold text-slate-700">Are you the primary policy holder?</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex gap-4"
                  >
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="yes" />
                      </FormControl>
                      <FormLabel className="font-medium cursor-pointer">Yes</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="no" />
                      </FormControl>
                      <FormLabel className="font-medium cursor-pointer">No</FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="insuredMemberName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Policy Holder Name</FormLabel>
                  <FormControl>
                    <Input placeholder="As it appears on card" {...field} className="h-11 rounded-xl shadow-sm border-slate-200 focus:border-emerald-500 focus:ring-emerald-50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="insuredMemberDob"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Holder Date of Birth</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} className="h-11 rounded-xl shadow-sm border-slate-200 focus:border-emerald-500 focus:ring-emerald-50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {isInsuredMember === "no" && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              className="space-y-6 p-4 rounded-2xl bg-slate-50/50 border border-slate-100"
            >
              <FormField
                control={form.control}
                name="relationshipWithInsuredMember"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-slate-700">Relationship to Policy Holder</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-11 rounded-xl shadow-sm border-slate-200">
                          <SelectValue placeholder="Select relationship" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="Spouse">Spouse</SelectItem>
                        <SelectItem value="Child">Child</SelectItem>
                        <SelectItem value="Parent">Parent</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="dependentName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Dependent Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Patient Name" {...field} className="h-11 rounded-xl shadow-sm border-slate-200 focus:border-emerald-500 focus:ring-emerald-50" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dependentDob"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Dependent Date of Birth</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} className="h-11 rounded-xl shadow-sm border-slate-200 focus:border-emerald-500 focus:ring-emerald-50" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="insuranceCompany"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Insurance Provider</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 rounded-xl shadow-sm border-slate-200">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-xl max-h-[300px]">
                      {INSURANCE_PROVIDERS.map((provider) => (
                        <SelectItem key={provider} value={provider}>
                          {provider}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="certificateNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Certificate / Policy #</FormLabel>
                  <FormControl>
                    <Input placeholder="Required or search if unsure" {...field} className="h-11 rounded-xl shadow-sm border-slate-200" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="groupNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Group Number</FormLabel>
                <FormControl>
                  <Input placeholder="Optional" {...field} className="h-11 rounded-xl shadow-sm border-slate-200" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            <div className="space-y-2">
              <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Insurance Card (Front)</FormLabel>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 rounded-xl border-dashed border-2 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all gap-2"
                  onClick={() => document.getElementById("card-front").click()}
                >
                  <Upload className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-600">
                    {insuranceCardFront ? insuranceCardFront.name : (existingFrontUrl ? "Front Uploaded" : "Upload Front")}
                  </span>
                </Button>
                <input
                  id="card-front"
                  type="file"
                  className="hidden"
                  onChange={(e) => setInsuranceCardFront(e.target.files[0])}
                />
                {(insuranceCardFront || existingFrontUrl) && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
              </div>
            </div>

            <div className="space-y-2">
              <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Insurance Card (Back)</FormLabel>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 rounded-xl border-dashed border-2 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all gap-2"
                  onClick={() => document.getElementById("card-back").click()}
                >
                  <Upload className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-600">
                    {insuranceCardBack ? insuranceCardBack.name : (existingBackUrl ? "Back Uploaded" : "Upload Back")}
                  </span>
                </Button>
                <input
                  id="card-back"
                  type="file"
                  className="hidden"
                  onChange={(e) => setInsuranceCardBack(e.target.files[0])}
                />
                {(insuranceCardBack || existingBackUrl) && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold transition-all shadow-md active:scale-[0.98]"
          >
            {submitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving Details...</span>
              </div>
            ) : (
              "Securely Save Insurance"
            )}
          </Button>
        </form>
      </Form>
    </motion.div>
  );
}
